/**
 * Hyperliquid public API client — called DIRECTLY from the browser.
 *
 * Hyperliquid's `info` endpoint is CORS-enabled, so the client hits it
 * straight from the frontend. That keeps market data off our Vercel edge
 * functions entirely (no per-invocation proxy cost) and gives us
 * Hyperliquid's fast, accurate on-exchange prices.
 *
 * Docs: https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api
 */

const HL_INFO_URL = 'https://api.hyperliquid.xyz/info'

async function hlPost<T>(body: unknown): Promise<T> {
	const res = await fetch(HL_INFO_URL, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(body)
	})
	if (!res.ok) {
		throw new Error(`hyperliquid ${res.status}`)
	}
	return res.json() as Promise<T>
}

/** One OHLC candle as returned by `candleSnapshot` (numeric fields are strings). */
export interface HlCandle {
	/** open time, ms */
	t: number
	/** close time, ms */
	T: number
	/** coin */
	s: string
	/** interval, e.g. "1h" */
	i: string
	o: string
	c: string
	h: string
	l: string
	/** volume */
	v: string
	/** number of trades */
	n: number
}

/**
 * Historical OHLC candles for a coin. `interval` is one of Hyperliquid's
 * supported buckets ("1m","5m","15m","1h","4h","12h","1d","1w"…).
 * `startTime`/`endTime` are unix ms. Hyperliquid caps a snapshot at 5000
 * candles, so callers size the interval to the range accordingly.
 */
export function fetchCandleSnapshot(
	coin: string,
	interval: string,
	startTime: number,
	endTime: number
): Promise<HlCandle[]> {
	return hlPost<HlCandle[]>({
		type: 'candleSnapshot',
		req: { coin, interval, startTime, endTime }
	})
}

/** Per-asset live context from `metaAndAssetCtxs` (numeric fields are strings). */
export interface HlAssetCtx {
	/** 24h notional volume on Hyperliquid */
	dayNtlVlm: string
	/** price 24h ago — basis for the 24h % change */
	prevDayPx: string
	/** current mark price */
	markPx: string
	midPx?: string
	oraclePx?: string
}

interface HlMeta {
	universe: { name: string }[]
}

/**
 * Live market contexts for every perp, keyed by coin name. One request
 * returns the whole universe; SWR dedupes it across all consumers.
 */
export async function fetchMarketContexts(): Promise<Map<string, HlAssetCtx>> {
	const [meta, ctxs] = await hlPost<[HlMeta, HlAssetCtx[]]>({
		type: 'metaAndAssetCtxs'
	})
	const map = new Map<string, HlAssetCtx>()
	meta.universe.forEach((u, i) => {
		const ctx = ctxs[i]
		if (ctx) map.set(u.name, ctx)
	})
	return map
}
